import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  BuildSection,
  BuildSectionResponse,
  BuildSectionsResponse,
  BuildSectionStats
} from '@/src/types/apps/buildSections'

interface BuildSectionsState {
  sections: BuildSection[]
  section: BuildSection | null
  stats: BuildSectionStats
  loading: boolean
  meta: BuildSectionsResponse['meta']
  error: unknown
  success: string | null
}

const initialState: BuildSectionsState = {
  sections: [],
  section: null,
  stats: { total: 0, published: 0, draft: 0 },
  loading: false,
  meta: { total: 0, pages: 0 },
  error: null,
  success: null
}

interface BuildSectionQueryOptions {
  take?: number
  skip?: number
  sortBy?: string
  filters?: Record<string, unknown>
}

export const getBuildSections = createAsyncThunk<
  BuildSectionsResponse,
  BuildSectionQueryOptions,
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygBuildSections/getBuildSections', async (params, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { get: Function }).get('build-sections', { params })

    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }

    return rejectWithValue(err.response?.data)
  }
})

export const getBuildSection = createAsyncThunk<
  BuildSectionResponse,
  number,
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygBuildSections/getBuildSection', async (id, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { get: Function }).get(`build-sections/${id}`)

    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }

    return rejectWithValue(err.response?.data)
  }
})

export const createBuildSection = createAsyncThunk<
  BuildSectionResponse,
  Partial<BuildSection> & { isPublished?: boolean },
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygBuildSections/createBuildSection', async (payload, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { post: Function }).post('build-sections', payload)

    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }

    return rejectWithValue(err.response?.data)
  }
})

export const updateBuildSection = createAsyncThunk<
  BuildSectionResponse,
  { id: number; data: Partial<BuildSection> & { isPublished?: boolean } },
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygBuildSections/updateBuildSection', async ({ id, data }, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { patch: Function }).patch(`build-sections/${id}`, data)

    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }

    return rejectWithValue(err.response?.data)
  }
})

export const deleteBuildSection = createAsyncThunk<
  { id: number },
  number,
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygBuildSections/deleteBuildSection', async (id, { rejectWithValue, extra }) => {
  try {
    await (extra.httpClient as { delete: Function }).delete(`build-sections/${id}`)

    return { id }
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }

    return rejectWithValue(err.response?.data)
  }
})

export const getBuildSectionStats = createAsyncThunk<
  BuildSectionStats,
  { country?: string },
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygBuildSections/getBuildSectionStats', async (params, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { get: Function }).get('build-sections/stats', { params })

    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }

    return rejectWithValue(err.response?.data)
  }
})

export const resetBuildSectionSuccessAndError = createAsyncThunk('gygBuildSections/resetSuccessAndError', async () => {
  return null
})

const gygBuildSectionsSlice = createSlice({
  name: 'gygBuildSections',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getBuildSections.pending, state => {
        state.loading = true
      })
      .addCase(getBuildSections.fulfilled, (state, action) => {
        state.loading = false
        state.sections = action.payload.data
        state.meta = action.payload.meta
      })
      .addCase(getBuildSections.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(getBuildSection.pending, state => {
        state.loading = true
      })
      .addCase(getBuildSection.fulfilled, (state, action) => {
        state.loading = false
        state.section = action.payload.data
      })
      .addCase(getBuildSection.rejected, state => {
        state.loading = false
      })
      .addCase(createBuildSection.fulfilled, state => {
        state.success = 'Build section created successfully'
      })
      .addCase(updateBuildSection.fulfilled, state => {
        state.success = 'Build section updated successfully'
      })
      .addCase(deleteBuildSection.fulfilled, (state, action) => {
        state.sections = state.sections.filter(section => section.id !== action.payload.id)
        if (state.meta?.total) {
          state.meta.total -= 1
        }
        state.success = 'Build section deleted successfully'
      })
      .addCase(getBuildSectionStats.fulfilled, (state, action) => {
        state.stats = action.payload
      })
      .addCase(resetBuildSectionSuccessAndError.fulfilled, state => {
        state.success = null
        state.error = null
      })
  }
})

export default gygBuildSectionsSlice.reducer
